import { ChangeDetectorRef, Component, NgZone } from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CountriesService } from 'src/app/core/services/countries.service';
import { NavigationService } from 'src/app/core/services/navigation.service';
import { WhiteLabelService } from 'src/app/core/services/white-label.service';
import { CompanyBranding, IpInfo } from 'src/app/core/types';
import {
  NUMERIC_VALIDATORS,
  validateCheckboxRequired,
  validateEqual,
} from 'src/app/core/validators/validators';
import { isMatchingControl } from 'src/app/shared/validators/validators';
import { AuthorizationService } from './../services/authorization.service';

export function atLeastOneElement(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as any[];
    return value && value.length > 0 ? null : { atLeastOneElement: true };
  };
}

export interface RegistrationRequest {
  lang: string;
  email: string;
  password: string;
  retypedPassword: string;
  country: string;
  userActivity: {
    type: 'manufacturer' | 'transportation_company' | 'forwarding' | 'education';
    scope:
      | 'sea_freight'
      | 'road_freight'
      | 'air_freight'
      | 'sea_transport'
      | 'road_transport'
      | 'air_transport'
      | 'furniture_industry'
      | 'construction'
      | 'food_industry'
      | 'industrial_automation'
      | 'automotive'
      | 'paper_industry'
      | 'other'
      | 'school'
      | 'university'
      | 'trainingCenter';
    position: 'employee' | 'manager' | 'owner' | 'student' | 'lecturer';
    city?: string;
    organizationName: string;
    taxId: string;
  };
  acceptedConsents: {
    type: string;
    isAccepted: boolean;
  }[];
}

const ACTIVITY_TYPES: string[] = [
  'forwarding',
  'transportation_company',
  'manufacturer',
  'education',
];
const ACTIVITY_AREA_SPEDITION: string[] = ['sea_freight', 'road_freight', 'air_freight'];
const ACTIVITY_AREA_TRANSPORTATION: string[] = ['road_transport', 'sea_transport', 'air_transport'];
const ACTIVITY_AREA_PRODUCER: string[] = [
  'furniture_industry',
  'construction',
  'food_industry',
  'industrial_automation',
  'automotive',
  'paper_industry',
  'other',
];
const ACTIVITY_AREA_EDUCATION: string[] = ['school', 'university', 'training_center'];
const ACTIVITY_AREA_OTHER: string[] = ['other'];
const POSITION = ['employee', 'manager', 'owner'];
const EDU_ROLES = ['lecturer', 'student'];
export const ROLES: string[] = [
  'forwarding',
  'transportation_company',
  'manufacturer',
  'education',
];

@Component({
    selector: 'gl-registration',
    templateUrl: './registration.component.html',
    styleUrls: ['./registration.component.scss'],
    standalone: false
})
export class RegistrationComponent {
  public formPart1: UntypedFormGroup;
  public isFormPart1Submitted = false;
  public formPart2: UntypedFormGroup;
  public roles: string[] = ROLES;
  public activityTypes: string[] = ACTIVITY_TYPES;
  public activityAreas: string[] = [];
  public positions: string[] = [];
  public isEdu = false;
  public success: boolean;
  public code: string;
  public currentLang: string;
  public privacyPolicyLink: string;
  public regulationsLink: string;
  public successMessage: string;
  public isLpCampaign: boolean;
  public isMobile: boolean;
  public isQueryEmail: boolean;
  public countries: string[] = [];
  public currentCountry: string;
  public whiteLabel: CompanyBranding;
  public isFirstPart = true;
  public isSecondPart = false;

  constructor(
    private authorizationService: AuthorizationService,
    private translate: TranslateService,
    private navigationService: NavigationService,
    private router: Router,
    private route: ActivatedRoute,
    private ngZone: NgZone,
    private whiteLabelService: WhiteLabelService,
    private cd: ChangeDetectorRef,
    private countriesService: CountriesService,
  ) {
    this.whiteLabel = this.whiteLabelService.getState();
    this.formPart1 = this.generatePart1Form();
    this.formPart2 = this.generatePart2Form();
    this.currentLang = this.countriesService.normalizeLanguageCode(this.translate.currentLang);
    this.countries = this.countriesService.getCountries(this.currentLang);
    this.privacyPolicyLink = `https://www.goodloading.com/${this.currentLang}/privacy-policy/`;
    this.regulationsLink = `https://www.goodloading.com/${this.currentLang}/regulations/`;
    this.translate.onLangChange?.subscribe((result: { lang: string }) => {
      this.currentLang = this.countriesService.normalizeLanguageCode(result.lang);
      this.privacyPolicyLink = `https://www.goodloading.com/${this.currentLang}/privacy-policy/`;
      this.regulationsLink = `https://www.goodloading.com/${this.currentLang}/regulations/`;
      this.countries = this.countriesService.getCountries(this.currentLang);
      this.formPart2
        .get('country')
        .setValue(this.countriesService.getCountryName(this.currentCountry, this.currentLang));
    });
    this.formPart2.get('country').valueChanges.subscribe((value) => {
      this.currentCountry = this.countriesService.getAlpha2Code(value, this.currentLang);
      this.formPart2.get('vatId').updateValueAndValidity();
    });

    this.authorizationService.getCountryFromIp().subscribe((data: IpInfo) => {
      this.currentCountry = data.country;
      this.formPart2
        .get('country')
        .setValue(this.countriesService.getCountryName(this.currentCountry, this.currentLang));
    });

    this.isMobile = this.router.url.includes('mobile');
    this.successMessage = this.isMobile
      ? 'mobile_register_success_message'
      : 'register_success_message';
    this.isLpCampaign = this.route.snapshot.data.lp ? true : false;
    const queryMethod = this.route.snapshot.queryParamMap.get('method');
    const queryEmail = this.route.snapshot.queryParamMap.get('email');
    if (queryMethod === 'email' || queryEmail) {
      this.isQueryEmail = true;
      this.formPart1.get('email').setValue(queryEmail);
      void this.router.navigate([], {
        relativeTo: route,
        queryParams: { email: null },
        queryParamsHandling: 'merge',
      });
    }

  }

  public activityTypeChange(selectedActivityType: string): void {
    switch (selectedActivityType) {
      case 'forwarding':
        this.activityAreas = ACTIVITY_AREA_SPEDITION;
        this.positions = POSITION;
        this.isEdu = false;
        break;
      case 'transportation_company':
        this.activityAreas = ACTIVITY_AREA_TRANSPORTATION;
        this.positions = POSITION;
        this.isEdu = false;
        break;
      case 'manufacturer':
        this.activityAreas = ACTIVITY_AREA_PRODUCER;
        this.positions = POSITION;
        this.isEdu = false;
        break;
      case 'education':
        this.activityAreas = ACTIVITY_AREA_EDUCATION;
        this.positions = EDU_ROLES;
        this.isEdu = true;
        break;
      case 'other':
        this.positions = POSITION;
        this.activityAreas = ACTIVITY_AREA_OTHER;
        this.isEdu = false;
        break;
      default:
        this.activityAreas = [];
        break;
    }
    this.formPart1.get('activityArea').patchValue(null);
    this.formPart1.get('activityType').patchValue(selectedActivityType);
  }

  public loginWithFacebook() {
    this.authorizationService.loginWithFacebook().subscribe((result) => {
      if (result && this.isMobile) {
        this.successMessage = 'mobile_account_activation_message';
        this.ngZone.run(() => this.handleRegisterSuccess());
      }
    });
  }

  public loginWithGoogle() {
    this.authorizationService.loginWithGoogle().subscribe((result) => {
      if (result && this.isMobile) {
        this.successMessage = 'mobile_account_activation_message';
        this.ngZone.run(() => this.handleRegisterSuccess());
      }
    });
  }

  public loginWithTrans() {
    this.authorizationService.redirectToTransLogin();
  }

  public onSubmitPart1(): void {
    if (this.formPart1.valid) {
      this.isFormPart1Submitted = true;
      this.isFirstPart = false;
      this.isSecondPart = true;
    }
  }

  public backButtonClick(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.isFormPart1Submitted = false;
    this.backToFirstStep();
  }

  private backToFirstStep(): void {
    this.isFirstPart = true;
    this.isSecondPart = false;
  }

  public onSubmitPart2(): void {
    if (this.formPart1.valid && this.formPart2.valid) {
      const registrationData: RegistrationRequest = {
        lang: this.currentLang,
        email: this.formPart1.value.email,
        password: this.formPart1.value.password,
        retypedPassword: this.formPart1.value.retypedPassword,
        country: this.countriesService.getAlpha2Code(this.formPart2.value.country, this.currentLang),
        userActivity: {
          type: this.formPart1.value.activityType,
          scope: this.formPart1.value.activityArea,
          position: this.formPart2.value.position,
          organizationName: this.formPart2.value.organizationName,
          taxId: this.formPart2.value.vatId === null ? undefined : this.formPart2.value.vatId,
          city: this.formPart2.value.city,
        },
        acceptedConsents: [
          {
            type: 'termsOfServiceAndPrivacyPolicy',
            isAccepted: this.formPart2.value.acceptedTermsOfServiceAndPrivacyPolicy,
          },
        ],
      };
      this.authorizationService.register(registrationData).subscribe(
        () => this.handleRegisterSuccess(),
        (error: ErrorEvent) => this.handleRegisterError(error),
      );
    }
  }

  public onLogin() {
    this.navigationService.navigateToLogin();
  }

  public getTranslationObject() {
    return { email: this.formPart1.controls.email.value };
  }

  public goTo(event: MouseEvent, link: string) {
    event.stopPropagation();
    event.preventDefault();
    window.open(link, '_blank');
  }

  public onSearch(value: string) {
    const allCountries = this.countriesService.getCountries(this.currentLang);
    this.countries = this.countriesService.filterCountries(allCountries, value);
  }

  private handleRegisterError(error: ErrorEvent): void {
    this.backToFirstStep();
    setTimeout(() => {
      if (error.error === 'wrong_email_format') {
        this.formPart1.controls.email.setErrors({ email: true });
      } else if (error.error === 'email_already_exists') {
        this.formPart1.controls.email.setErrors({ emailAlreadyExists: true });
      } else if (error.error === 'wrong_passsword_format') {
        this.formPart1.controls.password.setErrors({ wrongPassword: true });
      } else if (error.error === 'no_role') {
        this.formPart1.controls.activityType.setErrors({ noRole: true });
      }
      this.formPart1.markAllAsTouched();
      this.isFormPart1Submitted = true;
      this.cd.detectChanges();
    });
  }

  private handleRegisterSuccess() {
    this.success = true;
  }

  private isPasswordValid(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isValid: boolean = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(control.value);
      return isValid ? null : { wrongPassword: true };
    };
  }

  private generatePart1Form(): UntypedFormGroup {
    const password: UntypedFormControl = new UntypedFormControl(null, [
      this.isPasswordValid(),
      Validators.required,
    ]);
    const repeatedPassword: UntypedFormControl = new UntypedFormControl(null, [
      Validators.required,
      isMatchingControl(password),
    ]);
    return new UntypedFormGroup(
      {
        activityType: new UntypedFormControl(null, [Validators.required]),
        activityArea: new UntypedFormControl([], [Validators.required, atLeastOneElement()]),
        email: new UntypedFormControl(null, [Validators.required, Validators.email]),
        password: password,
        retypedPassword: repeatedPassword,
      },
      { updateOn: 'submit' },
    );
  }

  private generatePart2Form(): UntypedFormGroup {
    return new UntypedFormGroup(
      {
        position: new UntypedFormControl(null, [Validators.required]),
        organizationName: new UntypedFormControl(null, [Validators.required]),
        vatId: new UntypedFormControl(null, [
          this.conditionalValidator(
            () => this.currentCountry && this.currentCountry === 'PL' && !this.isEdu,
            Validators.compose([
              Validators.required,
              NUMERIC_VALIDATORS.integer,
              validateEqual(10),
            ])
          ),
          // NUMERIC_VALIDATORS.integer,
        ]),
        city: new UntypedFormControl(null, [
          this.conditionalValidator(() => this.isEdu, Validators.required),
        ]),
        country: new UntypedFormControl(null, [Validators.required]),
        acceptedTermsOfServiceAndPrivacyPolicy: new UntypedFormControl(false, [validateCheckboxRequired]),
      },
      { updateOn: 'submit' },
    );
  }

  private conditionalValidator(condition: () => boolean, validator: ValidatorFn): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!condition()) {
        return null;
      }
      return validator(control);
    };
  }
}
